import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "@/lib/toast";
import { useWakeLock } from "@/hooks/useWakeLock";

/**
 * Smart cooking timers — premium feature.
 *
 * Design notes:
 *  - Source of truth for remaining time is `endsAt` (epoch ms). The 1-second
 *    tick is only a re-render trigger; we never decrement a counter, so the
 *    timer survives tab throttling / sleep without drift.
 *  - Persisted to localStorage so reloads (and unintended navigations) don't
 *    lose an active timer mid-cook.
 *  - Wake Lock is acquired automatically while ≥1 timer is running.
 */

export type TimerStatus = "running" | "paused" | "finished";

export type Timer = {
  id: string;
  label: string;
  totalSeconds: number;
  /** Epoch ms at which the timer should fire. Updated when paused/resumed. */
  endsAt: number;
  /** Remaining ms captured at pause time — restored on resume. */
  pausedRemainingMs: number | null;
  status: TimerStatus;
  /** Optional metadata so the dock can show context and chips can find their timer. */
  recipeId?: string;
  recipeTitle?: string;
  stepIndex?: number;
  /** A stable key derived from (recipeId, stepIndex, original duration text)
   * so a chip can re-discover its own timer after a re-render. */
  matchKey?: string;
  /** True once we've fired the chime/toast for this expiry. */
  notified?: boolean;
};

type StartArgs = {
  label: string;
  seconds: number;
  recipeId?: string;
  recipeTitle?: string;
  stepIndex?: number;
  matchKey?: string;
};

type Ctx = {
  timers: Timer[];
  start: (args: StartArgs) => string;
  pause: (id: string) => void;
  resume: (id: string) => void;
  addSeconds: (id: string, n: number) => void;
  dismiss: (id: string) => void;
  findByMatchKey: (matchKey: string) => Timer | undefined;
  /** True when at least one timer is finished and the alarm is actively trying to alert. */
  alarmActive: boolean;
  /** True when the browser blocked our audible alarm (muted tab, autoplay policy, no AudioContext). */
  alarmMuted: boolean;
  /** True when audio output looks suppressed (suspended AudioContext, no Web Audio support, or a previous alarm was muted). Surface as a pre-emptive warning when starting timers so the user can unmute before the alarm fires. */
  audioLikelyMuted: boolean;
  /** Stop the looping alarm sound/vibration without dismissing the finished timers. */
  stopAlarm: () => void;
};

const TimerContext = createContext<Ctx | null>(null);

const STORAGE_KEY = "cp_timers_v1";

const loadFromStorage = (): Timer[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Timer[];
    if (!Array.isArray(parsed)) return [];
    // Reconcile statuses for timers whose `endsAt` already passed while away.
    const now = Date.now();
    return parsed.map((t) => {
      if (t.status === "running" && t.endsAt <= now) {
        return { ...t, status: "finished" as const };
      }
      return t;
    });
  } catch {
    return [];
  }
};

const saveToStorage = (timers: Timer[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(timers));
  } catch {
    // ignore quota / privacy mode
  }
};

const newId = () =>
  `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Singleton AudioContext, primed on the first user gesture (timer start /
 * resume). iOS Safari requires audio to be unlocked by a recent user
 * interaction — if we lazily construct a context inside `startAlarm()` at
 * expiry time, the timer often "rings silently". By priming once on a tap
 * and reusing the same context (resuming it if iOS later suspends it on
 * background), the alarm reliably plays.
 */
let primedCtx: AudioContext | null = null;
let primedMuted = false;

const ensurePrimedAudioContext = () => {
  if (primedCtx) {
    if (primedCtx.state === "suspended") {
      primedCtx.resume().catch(() => {});
    }
    return;
  }
  try {
    const Ctor =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      primedMuted = true;
      return;
    }
    const ctx = new Ctor();
    primedCtx = ctx;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    // Play a near-silent tick to fully unlock the iOS audio output.
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.02);
    } catch {
      // ignore
    }
  } catch {
    primedMuted = true;
  }
};

const ensureNotificationPermission = () => {
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  } catch {
    // ignore
  }
};

const fireExpiryNotification = (title: string, body: string) => {
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      return; // user is here — toast + audio is enough.
    }
    const n = new Notification(title, { body, tag: "cp-timer", requireInteraction: false });
    setTimeout(() => n.close(), 15_000);
  } catch {
    // ignore
  }
};

/**
 * Looping cooking alarm. Returns a controller exposing stop() + a `muted` flag
 * that is true if the browser blocked playback (autoplay policy, muted tab,
 * unavailable AudioContext). The alarm rings for up to 60s and naturally stops.
 *
 * Volume target: 0.7 — noticeably louder than the previous one-shot 0.4 chime.
 */
type AlarmController = {
  stop: () => void;
  muted: boolean;
};

const startAlarm = (): AlarmController => {
  let stopped = false;
  let muted = primedMuted;
  let vibrateInterval: number | null = null;
  let autoStopTimeout: number | null = null;
  // Track every scheduled oscillator/gain so stop() can actually silence
  // sounds that were pre-scheduled on the AudioContext timeline. Without
  // this, setting a JS `stopped` flag does nothing — Web Audio events are
  // already queued and will fire regardless.
  const scheduled: { osc: OscillatorNode; gain: GainNode }[] = [];

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (vibrateInterval !== null) {
      window.clearInterval(vibrateInterval);
      vibrateInterval = null;
    }
    if (autoStopTimeout !== null) {
      window.clearTimeout(autoStopTimeout);
      autoStopTimeout = null;
    }
    const ctx = primedCtx;
    const now = ctx ? ctx.currentTime : 0;
    for (const { osc, gain } of scheduled) {
      try {
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(0.0001, now);
      } catch {
        // ignore
      }
      try {
        osc.stop(now);
      } catch {
        // already stopped — ignore
      }
      try {
        osc.disconnect();
        gain.disconnect();
      } catch {
        // ignore
      }
    }
    scheduled.length = 0;
    try {
      (navigator as Navigator & { vibrate?: (p: number | number[]) => boolean }).vibrate?.(0);
    } catch {
      // ignore
    }
    // Don't close the primed AudioContext — we want to reuse it for the
    // next alarm without needing another user gesture.
  };

  // Audio: looping pattern of three beeps every ~2s for up to 60s.
  // Reuses the singleton context primed on user gesture.
  try {
    if (!primedCtx) {
      // No context primed (first alarm before any tap?) — try once now.
      ensurePrimedAudioContext();
    }
    const ctx = primedCtx;
    if (!ctx) {
      muted = true;
    } else {
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
        if (ctx.state === "suspended") muted = true;
      }
      const beep = (offset: number) => {
        if (stopped) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.value = 880;
        const t0 = ctx.currentTime + offset;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.7, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.45);
        osc.start(t0);
        osc.stop(t0 + 0.5);
        scheduled.push({ osc, gain });
      };
      // Schedule ~30 cycles (~60s).
      for (let cycle = 0; cycle < 30; cycle += 1) {
        const base = cycle * 2;
        beep(base);
        beep(base + 0.55);
        beep(base + 1.1);
      }
    }
  } catch {
    muted = true;
  }

  // Vibration: repeat every 2s for up to 60s. Best-effort.
  try {
    const nav = navigator as Navigator & { vibrate?: (p: number | number[]) => boolean };
    if (typeof nav.vibrate === "function") {
      const ok = nav.vibrate([400, 150, 400, 150, 400]);
      if (ok) {
        vibrateInterval = window.setInterval(() => {
          try {
            nav.vibrate?.([400, 150, 400, 150, 400]);
          } catch {
            // ignore
          }
        }, 2000);
      }
    }
  } catch {
    // ignore
  }

  // Auto-stop after 60s so a forgotten alarm doesn't ring forever.
  autoStopTimeout = window.setTimeout(stop, 60_000);

  return {
    stop,
    get muted() {
      return muted;
    },
  };
};


export const TimerProvider = ({ children }: { children: ReactNode }) => {
  const [timers, setTimers] = useState<Timer[]>(() => loadFromStorage());
  const notifiedRef = useRef<Set<string>>(new Set());
  // Active alarm controller — non-null while the looping alarm is sounding.
  const alarmRef = useRef<AlarmController | null>(null);
  const [alarmActive, setAlarmActive] = useState(false);
  const [alarmMuted, setAlarmMuted] = useState(false);
  const [audioLikelyMuted, setAudioLikelyMuted] = useState(false);
  // Tick to force re-renders for live countdowns. We include `tick` in the
  // memoised context value's deps so consumers (TimerChip, TimerDock) actually
  // re-render every second — without this, the `timers` array reference is
  // stable between ticks and React skips the update.
  const [tick, setTick] = useState(0);

  const stopAlarm = useCallback(() => {
    alarmRef.current?.stop();
    alarmRef.current = null;
    setAlarmActive(false);
    setAlarmMuted(false);
  }, []);

  // Persist whenever timers change.
  useEffect(() => {
    saveToStorage(timers);
  }, [timers]);

  // 1-second tick — only mounts when there's at least one running timer.
  useEffect(() => {
    const hasRunning = timers.some((t) => t.status === "running");
    if (!hasRunning) return;
    const id = window.setInterval(() => setTick((n) => (n + 1) % 1_000_000), 1000);
    return () => window.clearInterval(id);
  }, [timers]);


  // Detect expirations and fire side effects exactly once per timer.
  useEffect(() => {
    if (timers.length === 0) return;
    const now = Date.now();
    let mutated = false;
    let newlyExpired = false;
    const next = timers.map((t) => {
      if (t.status === "running" && t.endsAt <= now && !t.notified) {
        if (!notifiedRef.current.has(t.id)) {
          notifiedRef.current.add(t.id);
          newlyExpired = true;
          // Skip the in-app toast — it overlaps the TimerDock's "Stop alarm"
          // button at the bottom of the screen. The dock itself surfaces the
          // finished timer prominently; we still fire a system notification
          // when the tab is hidden so backgrounded users get alerted.
          fireExpiryNotification(
            `Timer finished: ${t.label}`,
            t.recipeTitle ? `From ${t.recipeTitle}` : "Tap to return to CleanPlate.",
          );

        }
        mutated = true;
        return { ...t, status: "finished" as const, notified: true };
      }
      return t;
    });
    if (mutated) setTimers(next);

    // Start (or restart) the looping alarm when a new timer expires. Stops any
    // existing alarm first so we don't stack overlapping audio.
    if (newlyExpired) {
      alarmRef.current?.stop();
      const ctrl = startAlarm();
      alarmRef.current = ctrl;
      setAlarmActive(true);
      setAlarmMuted(ctrl.muted);
    }
  });

  // When all finished timers are dismissed, ensure the alarm is silenced too.
  useEffect(() => {
    const anyFinished = timers.some((t) => t.status === "finished");
    if (!anyFinished && alarmActive) {
      stopAlarm();
    }
  }, [timers, alarmActive, stopAlarm]);

  // Drop the pre-emptive mute warning once there are no timers left.
  useEffect(() => {
    if (timers.length === 0 && audioLikelyMuted) setAudioLikelyMuted(false);
  }, [timers, audioLikelyMuted]);

  // Stop alarm on unmount.
  useEffect(() => {
    return () => {
      alarmRef.current?.stop();
      alarmRef.current = null;
    };
  }, []);

  const start = useCallback((args: StartArgs): string => {
    // Prime the audio context on this user gesture so iOS lets us play the
    // alarm later when the timer expires (otherwise the AudioContext is
    // suspended and the alarm rings silently). Also opportunistically ask
    // for notification permission so a backgrounded tab can still alert.
    ensurePrimedAudioContext();
    ensureNotificationPermission();
    // Up-front mute check: if Web Audio is unavailable or the primed context
    // is still suspended after a user-gesture resume attempt, the alarm will
    // very likely fire silently. Surface this so the dock can warn the user
    // before they walk away from the kitchen.
    const likelyMuted = primedMuted || !primedCtx || primedCtx.state === "suspended";
    setAudioLikelyMuted(likelyMuted);
    const id = newId();
    const now = Date.now();
    setTimers((prev) => {
      // Replace any existing timer with the same matchKey (same chip restarted).
      const filtered = args.matchKey
        ? prev.filter((t) => t.matchKey !== args.matchKey)
        : prev;
      return [
        ...filtered,
        {
          id,
          label: args.label,
          totalSeconds: args.seconds,
          endsAt: now + args.seconds * 1000,
          pausedRemainingMs: null,
          status: "running",
          recipeId: args.recipeId,
          recipeTitle: args.recipeTitle,
          stepIndex: args.stepIndex,
          matchKey: args.matchKey,
        },
      ];
    });
    toast(`Timer started: ${args.label}`, { duration: 2500 });
    return id;
  }, []);

  const pause = useCallback((id: string) => {
    setTimers((prev) =>
      prev.map((t) => {
        if (t.id !== id || t.status !== "running") return t;
        const remaining = Math.max(0, t.endsAt - Date.now());
        return { ...t, status: "paused", pausedRemainingMs: remaining };
      }),
    );
  }, []);

  const resume = useCallback((id: string) => {
    ensurePrimedAudioContext();
    setTimers((prev) =>
      prev.map((t) => {
        if (t.id !== id || t.status !== "paused" || t.pausedRemainingMs == null) return t;
        return {
          ...t,
          status: "running",
          endsAt: Date.now() + t.pausedRemainingMs,
          pausedRemainingMs: null,
        };
      }),
    );
  }, []);

  const addSeconds = useCallback((id: string, n: number) => {
    setTimers((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        if (t.status === "finished") {
          // Re-arm the timer for `n` more seconds.
          notifiedRef.current.delete(t.id);
          return {
            ...t,
            status: "running",
            endsAt: Date.now() + n * 1000,
            pausedRemainingMs: null,
            notified: false,
          };
        }
        if (t.status === "paused" && t.pausedRemainingMs != null) {
          return { ...t, pausedRemainingMs: t.pausedRemainingMs + n * 1000 };
        }
        return { ...t, endsAt: t.endsAt + n * 1000 };
      }),
    );
  }, []);

  const dismiss = useCallback((id: string) => {
    notifiedRef.current.delete(id);
    setTimers((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const findByMatchKey = useCallback(
    (matchKey: string) => timers.find((t) => t.matchKey === matchKey),
    [timers],
  );

  const hasRunning = timers.some((t) => t.status === "running");
  useWakeLock(hasRunning);

  const value = useMemo<Ctx>(
    () => ({
      timers,
      start,
      pause,
      resume,
      addSeconds,
      dismiss,
      findByMatchKey,
      alarmActive,
      alarmMuted,
      audioLikelyMuted,
      stopAlarm,
    }),
    // `tick` is intentionally included so consumers re-render every second
    // for live countdowns even though the `timers` array reference is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timers, tick, start, pause, resume, addSeconds, dismiss, findByMatchKey, alarmActive, alarmMuted, audioLikelyMuted, stopAlarm],
  );

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};

export const useTimers = (): Ctx => {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error("useTimers must be used inside <TimerProvider>");
  return ctx;
};

/** Compute remaining seconds for any timer status. Pure helper for UI. */
export function getRemainingSeconds(t: Timer): number {
  if (t.status === "finished") return 0;
  if (t.status === "paused") return Math.ceil((t.pausedRemainingMs ?? 0) / 1000);
  return Math.max(0, Math.ceil((t.endsAt - Date.now()) / 1000));
}
